import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ChangeEvent } from 'react';
import { api, FalhaDaApi } from '../../api/cliente';
import { Alerta, Botao, Ficha } from '../../componentes/base';
import { Caixa, Campo, Seletor } from '../../componentes/campos';
import { Modal } from '../../componentes/Modal';
import { data } from '../../util/formato';

/** Espelha `ROTULO_DOCUMENTO` da API. */
const TIPOS: ReadonlyArray<{ valor: string; rotulo: string; exigeAssinatura: boolean }> = [
  { valor: 'CONTRATO', rotulo: 'Contrato de prestação de serviços', exigeAssinatura: true },
  { valor: 'DOCUMENTO_PESSOAL', rotulo: 'Documento pessoal (RG, CPF, CNH)', exigeAssinatura: false },
  { valor: 'COMPROVANTE_DIVIDA', rotulo: 'Comprovante do débito', exigeAssinatura: false },
  { valor: 'NOTA_FISCAL', rotulo: 'Nota fiscal', exigeAssinatura: false },
  { valor: 'ORCAMENTO', rotulo: 'Orçamento / plano de tratamento', exigeAssinatura: false },
  { valor: 'PRONTUARIO', rotulo: 'Ficha ou prontuário', exigeAssinatura: false },
  { valor: 'COMPROVANTE_RESIDENCIA', rotulo: 'Comprovante de residência', exigeAssinatura: false },
  { valor: 'OUTRO', rotulo: 'Outro documento', exigeAssinatura: false },
];

export interface DocumentoDoCliente {
  readonly id: string;
  readonly tipo: string;
  readonly rotuloTipo: string;
  readonly titulo: string | null;
  readonly nomeOriginal: string;
  readonly origem: string;
  readonly assinado: boolean;
  readonly conferido: boolean;
  readonly tamanhoLegivel: string;
  readonly criadoEm: string;
}

/**
 * Documentos do caso, no portal.
 *
 * Duas diferenças em relação à mesma lista no sistema do escritório, e as duas são
 * de propósito:
 *
 * 1. A clínica NÃO confere documento. Conferir é o ato pelo qual uma colaboradora
 *    atesta que o arquivo serve para ajuizar; deixar quem enviou atestar o próprio
 *    envio esvaziaria a verificação. O selo aparece, mas só de leitura.
 *
 * 2. A clínica só remove o que ELA enviou. O que o escritório juntou pode ser peça
 *    do processo — o servidor recusa, e o botão nem é oferecido.
 *
 * A `PROCURAÇÃO` também está fora da lista de tipos: é peça que o escritório colhe.
 */
export function DocumentosDoCliente({
  devedorId,
  documentos,
}: {
  devedorId: string;
  documentos: readonly DocumentoDoCliente[];
}) {
  const clienteConsulta = useQueryClient();
  const [anexando, setAnexando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function recarregar() {
    void clienteConsulta.invalidateQueries({ queryKey: ['portal'] });
  }

  const excluir = useMutation({
    mutationFn: (id: string) => api.remover(`/portal/documentos/${id}`),
    onSuccess: () => {
      setErro(null);
      recarregar();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível remover o documento.'),
  });

  return (
    <>
      <div className="flex items-center justify-between gap-3 p-5 pb-2">
        <h2 className="text-lg text-[color:var(--texto)]">
          Documentos
          <span className="ml-3 text-xs font-normal text-[color:var(--texto-tenue)]">
            {documentos.length}
          </span>
        </h2>
        <Botao
          tipo="primario"
          onClick={() => setAnexando(true)}
          className="shrink-0 px-2.5 py-1 text-xs"
        >
          Anexar
        </Botao>
      </div>

      {erro && (
        <div className="px-5 pb-2">
          <Alerta tom="vencido">{erro}</Alerta>
        </div>
      )}

      {documentos.length === 0 ? (
        <p className="px-5 pb-5 text-sm text-[color:var(--texto-tenue)]">
          Nenhum documento anexado. Para cobrar — e sobretudo para ajuizar —, o
          escritório precisa do contrato, de um documento pessoal e do comprovante do
          débito.
        </p>
      ) : (
        <ul>
          {documentos.map((d) => (
            <li key={d.id} className="border-t px-5 py-3 text-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p
                    className="truncate font-medium text-[color:var(--texto)]"
                    title={d.nomeOriginal}
                  >
                    {d.titulo ?? d.nomeOriginal}
                  </p>
                  <p className="mt-0.5 text-xs text-[color:var(--texto-tenue)]">
                    {d.rotuloTipo}
                    {d.assinado ? ' · assinado' : ''}
                    {' · '}
                    {d.tamanhoLegivel}
                    {' · '}
                    {d.origem === 'CLINICA' ? 'enviado pela clínica' : 'juntado pelo escritório'}
                    {' em '}
                    {data(d.criadoEm)}
                  </p>
                </div>
                {d.conferido ? (
                  <Ficha tom="recebido">Conferido</Ficha>
                ) : (
                  <Ficha tom="atencao">Em conferência</Ficha>
                )}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <a
                  href={`/api/portal/documentos/${d.id}/arquivo`}
                  target="_blank"
                  rel="noreferrer"
                  className="rounded-md border px-2.5 py-1 text-xs text-[color:var(--texto)] hover:border-[color:var(--acento)] hover:text-[color:var(--acento)]"
                >
                  Abrir
                </a>
                {/* Só o que a própria clínica enviou. O botão some no resto porque o
                    servidor recusaria — oferecer e falhar é pior do que não oferecer. */}
                {d.origem === 'CLINICA' && (
                  <Botao
                    tipo="discreto"
                    onClick={() => {
                      if (window.confirm(`Remover “${d.nomeOriginal}”?`)) excluir.mutate(d.id);
                    }}
                    desabilitado={excluir.isPending}
                    className="px-0 text-xs"
                  >
                    Remover
                  </Botao>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ModalAnexar
        aberto={anexando}
        devedorId={devedorId}
        aoFechar={() => setAnexando(false)}
        aoSucesso={() => {
          setAnexando(false);
          recarregar();
        }}
      />
    </>
  );
}

/** Espelha `ARMAZENAMENTO_LIMITE_MB` da API. */
const LIMITE_MB = 25;

const ACEITOS = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.gif,.doc,.docx,.odt,.txt,.xls,.xlsx,.csv';

function ModalAnexar({
  aberto,
  devedorId,
  aoFechar,
  aoSucesso,
}: {
  aberto: boolean;
  devedorId: string;
  aoFechar: () => void;
  aoSucesso: () => void;
}) {
  const inputArquivo = useRef<HTMLInputElement>(null);
  const [tipo, setTipo] = useState('CONTRATO');
  const [titulo, setTitulo] = useState('');
  const [assinado, setAssinado] = useState(false);
  const [arquivo, setArquivo] = useState<{ nome: string; base64: string } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const exigeAssinatura = TIPOS.find((t) => t.valor === tipo)?.exigeAssinatura ?? false;

  function resetar() {
    setTipo('CONTRATO');
    setTitulo('');
    setAssinado(false);
    setArquivo(null);
    setErro(null);
    if (inputArquivo.current) inputArquivo.current.value = '';
  }

  function fechar() {
    resetar();
    aoFechar();
  }

  const anexar = useMutation({
    mutationFn: () => {
      if (!arquivo) throw new Error('Selecione um arquivo.');
      return api.criar(`/portal/devedores/${devedorId}/documentos`, {
        nome: arquivo.nome,
        base64: arquivo.base64,
        tipo,
        ...(titulo.trim() ? { titulo: titulo.trim() } : {}),
        assinado: exigeAssinatura ? assinado : false,
      });
    },
    onSuccess: () => {
      resetar();
      aoSucesso();
    },
    onError: (e) =>
      setErro(e instanceof FalhaDaApi ? e.message : 'Não foi possível anexar o documento.'),
  });

  /* O arquivo vai em base64 no corpo JSON, como no resto do sistema. A recepção
     fotografa o documento pelo celular; o `FileReader` entrega o data URL e a API
     aceita tanto ele quanto o base64 puro. */
  function lerArquivo(evento: ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0];
    if (!escolhido) {
      setArquivo(null);
      return;
    }
    // Barrado aqui, antes de ler: um arquivo acima do limite só seria recusado
    // depois de subir inteiro — minutos de espera no 4G da recepção.
    if (escolhido.size > LIMITE_MB * 1024 * 1024) {
      setArquivo(null);
      evento.target.value = '';
      setErro(
        `Este arquivo tem ${(escolhido.size / 1024 / 1024).toFixed(1).replace('.', ',')} MB; ` +
          `o limite é ${LIMITE_MB} MB. Reduza o PDF ou envie as páginas em partes.`,
      );
      return;
    }
    setErro(null);
    const leitor = new FileReader();
    leitor.onload = () => setArquivo({ nome: escolhido.name, base64: String(leitor.result) });
    leitor.onerror = () => setErro('Não foi possível ler o arquivo.');
    leitor.readAsDataURL(escolhido);
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={fechar}
      titulo="Anexar documento"
      descricao="Contrato, documento pessoal, comprovante do débito e demais peças do caso."
      largura="32rem"
      rodape={
        <>
          <Botao onClick={fechar} desabilitado={anexar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            onClick={() => anexar.mutate()}
            desabilitado={anexar.isPending || !arquivo}
          >
            {anexar.isPending ? 'Enviando…' : 'Anexar'}
          </Botao>
        </>
      }
    >
      <div className="space-y-4">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        <Seletor
          rotulo="Tipo"
          valor={tipo}
          aoMudar={(v) => {
            setTipo(v);
            setAssinado(false);
          }}
          opcoes={TIPOS.map((t) => ({ valor: t.valor, rotulo: t.rotulo }))}
        />

        <div>
          <span className="mb-1 block text-xs font-medium text-[color:var(--texto-suave)]">
            Arquivo
          </span>
          <input
            ref={inputArquivo}
            type="file"
            accept={ACEITOS}
            onChange={lerArquivo}
            className="block w-full text-sm text-[color:var(--texto)] file:mr-3 file:rounded-md file:border file:bg-[color:var(--superficie)] file:px-3 file:py-1.5 file:text-sm file:text-[color:var(--texto)]"
          />
          {arquivo && (
            <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">{arquivo.nome}</p>
          )}
          <p className="mt-1 text-xs text-[color:var(--texto-tenue)]">
            PDF, foto ou arquivo do Office — até {LIMITE_MB} MB. Foto do celular serve.
          </p>
        </div>

        <Campo
          rotulo="Título (opcional)"
          valor={titulo}
          aoMudar={setTitulo}
          placeholder="Ex.: Contrato assinado em 12/03"
        />

        {exigeAssinatura && (
          <Caixa rotulo="Este documento já está assinado" marcado={assinado} aoMudar={setAssinado} />
        )}
      </div>
    </Modal>
  );
}
